import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const TaskDashboard = (props) => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [assigneeFilter, setAssigneeFilter] = useState('');
    const [pageSize, setPageSize] = useState(5);
    const filteredRows = rows.filter(row => {
        const matchesSearch = row.title.toLowerCase().includes(searchQuery.toLowerCase());
        const rowStatus = (row.status || '').toLowerCase().replace(/[\s-]/g, '');
        const filterStatus = statusFilter.toLowerCase().replace(/[\s-]/g, '');
        const matchesPriority = priorityFilter ? (row.priority || '').toLowerCase() === priorityFilter.toLowerCase() : true;
        const matchesAssignee = assigneeFilter ? String(row.assignee_id) === String(assigneeFilter) : true;
        const matchesStatus = statusFilter === 'overdue' ? (row.due_date && row.due_date !== '-' && new Date(row.due_date) < new Date() && rowStatus !== 'done') : (statusFilter ? rowStatus === filterStatus : true);
        return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
    });
    const metrics = {
        total: rows.length,
        inProgress: rows.filter(r => r.status === 'In Progress' || r.status === 'in-progress' ).length,
        completed: rows.filter(r => r.status === 'Done' || r.status === 'done').length,
        overdue: rows.filter(r => {
            if (!r.due_date || r.due_date === '-') return false;
            return new Date(r.due_date) < new Date() && r.status !== 'Done' && r.status !== 'done';
        }).length
    };

    // Prepare data for the Pie Chart based on filteredRows
    const pieData = [
        { name: 'To Do', value: filteredRows.filter(r => r.status === 'To Do' || r.status === 'todo' || r.status === 'Backlog' || r.status === 'backlog').length },
        { name: 'In Progress', value: filteredRows.filter(r => r.status === 'In Progress' || r.status === 'in-progress').length },
        { name: 'Completed', value: filteredRows.filter(r => r.status === 'Done' || r.status === 'done').length },
        { name: 'Overdue', value: filteredRows.filter(r => r.due_date && r.due_date !== '-' && new Date(r.due_date) < new Date() && r.status !== 'Done' && r.status !== 'done').length }
    ].filter(entry => entry.value > 0);
    const PIE_COLORS = ['#1976d2', '#ed6c02', '#2e7d32', '#d32f2f'];

    // Prepare data for the Bar Chart (Tasks per Assignee)
    const barDataMap = {};
    filteredRows.forEach(row => {
        const name = row.assignee || 'Unassigned';
        if(!barDataMap[name]) {
            barDataMap[name] = 0;
        }
        barDataMap[name] += 1;
    });

    // Convert the map object into an array for the chart
    const barData = Object.keys(barDataMap).map(key => ({
        name: key,
        tasks: barDataMap[key]
    }));

    // This function calls Django banckend
    const fetchTasks = () => {
        setLoading(true);
        getRequest(GET_URL.tasks.api, {
            limit: 100, pageno: 1
        }, props)
            .then(res => {
                if (res?.status === 200) {
                    const fetchedTasks = res.data?.data || res.data || [];
                    const formattedRows = fetchedTasks.map(task => ({
                        id: task.id,
                        title: task.title,
                        assignee: task.assigned_to_name || 'Unassigned',
                        assignee_id: task.assigned_to || '',
                        status: task.task_stage_name || 'To Do',
                        priority: task.priority || 'Medium',
                        due_date: task.deadline || '-',
                        completed_at: task.modified || task.updated_at || null
                    }));
                    setRows(formattedRows);
                }
            })
            .catch(err =>
                console.error("Failed to fetch tasks", err)
            )
            .finally(() =>
                setLoading(false));
    };
    useEffect(() => {
        fetchTasks();
        getRequest( GET_URL.user.api, { limit: 100 }, props)
        .then(res => {
            if(res?.status === 200){
                setUsers(res.data?.data || res.data || []);
            }
        })
        .catch(err => console.error("Failed to load users", err));
    }, []);
    
    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                Task Management Dashboard
            </Typography>
            <Grid container spacing={3} sx={{ p: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>

                {/* Total task card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} onClick={() => navigate('/task/list')} sx={{ p: 2, cursor:'pointer', borderLeft: '3px solid #1976d2', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'} }}>
                        <Typography variant="caption" color={"textFieldClasses.secondary"} fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                            Total Tasks
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                            {metrics.total}
                        </Typography>
                    </Paper>
                </Grid>

                {/* In Progress Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} onClick={() => navigate('/task/list?status=InProgress')} sx={{ p: 2, cursor:'pointer', borderLeft: '3px solid #ed6c02', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'} }}>
                        <Typography variant="caption" color={"text.secondary"} fontweight={600} sx={{ textTransform: 'uppercase' }}>
                            InProgress
                        </Typography>
                        <Typography variant="h4" fontweight={700} sx={{ mt: 1 }}>
                            {metrics.inProgress}
                        </Typography>
                    </Paper>
                </Grid>

                {/* Completed Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} onClick={() => navigate('/task/list?status=Done')} sx={{ p: 2, cursor:'pointer', borderLeft: '3px solid #2e7d32', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'} }}>
                        <Typography variant="caption" color={"text.secondary"} fontweight={600} sx={{ textTransform: 'upercase' }}>
                            Completed
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                            {metrics.completed}
                        </Typography>
                    </Paper>
                </Grid>

                {/* Overdue Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={1} onClick={() => navigate('/task/list?status=Overdue')} sx={{ p: 2, cursor:'pointer', borderLeft: '3px solid #d32f2f', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'} }}>
                        <Typography variant="caption" color={"text.secondary"} fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                            Overdue
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                            {metrics.overdue}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Pie Chart */}
            <Grid container spacing={3} sx={{ p:2, pt: 0, display: 'flex', flexDirection: 'row' }}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={1} sx={{ p: 2, height: 400, width: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <Typography variant="subtitle1" fontweight={600} gutterBottom>
                            Task Status Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index%PIE_COLORS.length]}/>
                                    ))}
                                </Pie>
                                <RechartsTooltip/>
                                <Legend/>
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Bar Chart (Tasks per user) */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={1} sx={{ p: 2, height: 400, width: 800, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Tasks per Assignee
                        </Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={barData} margin={{top: 10, right: 30, left: 0, bottom: 0}}>
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis dataKey="name"/>
                                <YAxis allowDecimal={false}/>
                                <RechartsTooltip/>
                                <Bar dataKey="tasks" fill="#1976d2" barSize={40}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TaskDashboard